function pickString(value) {
  return value === undefined || value === null ? '' : String(value).trim();
}

function pickNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeStringList(value) {
  if (!Array.isArray(value)) {
    return [];
  }

  return [...new Set(value.map(pickString).filter(Boolean))];
}

function formatEvidenceValue(value) {
  if (Array.isArray(value)) {
    return normalizeStringList(value).slice(0, 8).join(', ');
  }

  if (value === undefined || value === null || value === '') {
    return '';
  }

  return String(value);
}

const EVIDENCE_LABELS = {
  count: 'Count',
  affectedCount: 'Affected',
  actionIds: 'Actions',
  threshold: 'Threshold',
  observed: 'Observed',
  ruleVersion: 'Rule version',
  windowSeconds: 'Window seconds',
  windowMinutes: 'Window minutes',
  bucketId: 'Bucket',
  bucketKey: 'Bucket',
  bucketStart: 'Bucket start',
  bucketEnd: 'Bucket end',
  windowBucket: 'Window bucket',
  windowStart: 'Window start',
  windowEnd: 'Window end',
};

function mapCountSummary(counts) {
  if (!isPlainObject(counts)) {
    return '';
  }

  return Object.entries(counts)
    .map(([key, value]) => `${key}: ${pickNumber(value)}`)
    .join(', ');
}

function buildEvidenceSummary(evidence) {
  if (!isPlainObject(evidence)) {
    return [];
  }

  const items = [];
  Object.entries(EVIDENCE_LABELS).forEach(([key, label]) => {
    const formatted = formatEvidenceValue(evidence[key]);
    if (formatted) {
      items.push(`${label}: ${formatted}`);
    }
  });

  const counts = mapCountSummary(evidence.counts);
  if (counts) {
    items.push(`Counts: ${counts}`);
  }

  return items;
}

function resolveSampleProfileUids(alert, evidence) {
  const direct = normalizeStringList(alert?.sampleProfileUids);
  if (direct.length > 0) {
    return direct;
  }

  const evidenceSamples = normalizeStringList(evidence?.sampleProfileUids);
  if (evidenceSamples.length > 0) {
    return evidenceSamples;
  }

  return normalizeStringList(evidence?.profileUids);
}

export function mapSecurityAlertDto(alert) {
  const evidence = isPlainObject(alert?.evidence) ? alert.evidence : {};

  return {
    uid: pickString(alert?.uid),
    createdAt: pickString(alert?.createdAt),
    updatedAt: pickString(alert?.updatedAt),
    status: pickString(alert?.status || 'open'),
    severity: pickString(alert?.severity || 'low'),
    score: pickNumber(alert?.score),
    ruleId: pickString(alert?.ruleId),
    ruleName: pickString(alert?.ruleName),
    actorProfileUid: pickString(alert?.actorProfileUid),
    supportGrantId: alert?.supportGrantId === undefined || alert?.supportGrantId === null ? null : pickNumber(alert.supportGrantId),
    sessionId: pickString(alert?.sessionId),
    ip: pickString(alert?.ip),
    userAgent: pickString(alert?.userAgent),
    windowStart: pickString(alert?.windowStart),
    windowEnd: pickString(alert?.windowEnd),
    affectedCount: pickNumber(alert?.affectedCount),
    sampleProfileUids: resolveSampleProfileUids(alert, evidence),
    evidence,
    evidenceSummary: buildEvidenceSummary(evidence),
    suggestedResponse: pickString(alert?.suggestedResponse),
    acknowledgedByUid: pickString(alert?.acknowledgedByUid),
    acknowledgedAt: pickString(alert?.acknowledgedAt),
    resolvedByUid: pickString(alert?.resolvedByUid),
    resolvedAt: pickString(alert?.resolvedAt),
    resolutionNote: pickString(alert?.resolutionNote),
  };
}

export function mapSecurityAlertsListResponseToDto(response) {
  return {
    alerts: Array.isArray(response?.alerts) ? response.alerts.map(mapSecurityAlertDto) : [],
    total: pickNumber(response?.total),
  };
}

export function mapSecurityAlertDetailResponseToDto(response) {
  return mapSecurityAlertDto(response?.alert || response);
}
